import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { FilterSelect } from "../components/FilterSelect";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import { SplitLines, EASE } from "../components/Reveal";
import { fetchOptions, createEnquiry } from "../lib/api";
import { BUSINESS, waLink } from "../data/business";
import { usePageMeta } from "../lib/usePageMeta";

const Field = ({ id, label, ...props }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="eyebrow">{label}</label>
    <input id={id} data-testid={`enquiry-${id}`} className="field" {...props} />
  </div>
);

export default function Enquiry() {
  usePageMeta(
    "Diamond Enquiry on WhatsApp | Buy Lab Grown Diamonds Online India – Padmavati Gem & Jewelss",
    "Order lab grown diamonds on WhatsApp: send shape, sieve mm, clarity and carats for a quotation, parcel photos and IGI certificate. Lab grown solitaire, ring, earring and pendant enquiries welcome."
  );
  const [sp] = useSearchParams();
  const [options, setOptions] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", buyer_type: sp.get("buyer") || "B2C",
    shape: sp.get("shape") || "", sieve: sp.get("sieve") || "", clarity: sp.get("clarity") || "",
    carat_range: sp.get("carat") || "", quantity_carats: "", lot_id: sp.get("lot") || "", message: "",
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  useEffect(() => { fetchOptions().then(setOptions).catch(() => {}); }, []);

  const label = (list, v) => list?.find((o) => o.value === v)?.label || v;
  const buildMessage = () => [
    `Hello ${BUSINESS.name}, I'd like a quote.`,
    `Name: ${form.name}`, `Type: ${form.buyer_type}`,
    form.lot_id && `Lot: ${form.lot_id}`,
    form.shape && `Shape: ${form.shape}`,
    form.sieve && `Sieve/mm: ${label(options?.sieves, form.sieve)}`,
    form.clarity && `Clarity: ${form.clarity}`,
    form.carat_range && `Carat: ${label(options?.carats, form.carat_range)}`,
    form.quantity_carats && `Quantity: ${form.quantity_carats} ct`,
    form.message && `Note: ${form.message}`,
  ].filter(Boolean).join("\n");

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.phone.trim().length < 8) { toast.error("Please enter your name and a valid phone number."); return; }
    setSending(true);
    try {
      const payload = { ...form, quantity_carats: form.quantity_carats ? Number(form.quantity_carats) : null };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const saved = await createEnquiry(payload);
      setDone(saved);
      toast.success("Enquiry saved — opening chat");
      window.open(waLink(buildMessage()), "_blank", "noopener");
    } catch {
      toast.error("Could not save the enquiry. Please use the direct chat link.");
    } finally { setSending(false); }
  };

  return (
    <main data-testid="enquiry-page" className="pt-[72px] min-h-screen grain">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-6 flex items-center gap-2"><WhatsAppIcon size={14} className="text-[#25D366]" /> enquiry</p>
          <h1 className="font-display font-light text-ivory text-[clamp(2.6rem,6vw,5.5rem)] leading-[0.95]">
            <SplitLines lines={["Tell us the spec.", <span key="a" className="text-saffron italic">We quote in minutes.</span>]} />
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-8 text-ivory/60 font-light leading-relaxed max-w-md">
            Fill in what you know — shape, sieve size in mm, clarity, carat and quantity in carats. We save the enquiry and open <WhatsAppIcon size={16} className="text-[#25D366]" />
            with a pre-written message to <span className="text-ivory">{BUSINESS.phone}</span>.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-10 border-l-2 border-saffron pl-5 space-y-2 text-sm text-ivory/60">
            <p className="text-ivory">What we send back</p>
            <p>· Quote & availability</p><p>· Parcel / stone photo or video</p><p>· IGI certificate (solitaires)</p><p>· GST invoice on confirmation</p>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.3 }} className="lg:col-span-7">
          {done ? (
            <div data-testid="enquiry-success" className="border border-gold/30 bg-[#0e0e0e] p-10 text-center">
              <CheckCircle2 size={44} strokeWidth={1.2} className="mx-auto text-saffron" />
              <h2 className="font-display text-4xl text-ivory mt-6">Enquiry received</h2>
              <p className="text-ivory/60 mt-3 font-light">Reference <span className="text-saffron font-mono text-sm">{done.id}</span>. If <WhatsAppIcon size={15} className="text-[#25D366]" /> didn't open, tap below.</p>
              <a href={waLink(buildMessage())} target="_blank" rel="noreferrer" data-testid="enquiry-open-whatsapp-btn" className="btn-gold mt-8"><WhatsAppIcon size={16} /> Open</a>
              <button data-testid="enquiry-new-btn" onClick={() => setDone(null)} className="block mx-auto mt-6 text-xs uppercase tracking-[0.2em] text-ivory/50 hover:text-saffron">Send another</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="enquiry-form" className="border border-gold/20 bg-[#0e0e0e] p-6 sm:p-10 space-y-8">
              <div className="flex gap-2" data-testid="buyer-type-toggle">
                {["B2C", "B2B"].map((t) => (
                  <button type="button" key={t} data-testid={`buyer-type-${t}`} onClick={() => setForm((f) => ({ ...f, buyer_type: t }))}
                    className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] border transition-colors ${form.buyer_type === t ? "bg-saffron text-night border-saffron" : "border-gold/30 text-ivory/70 hover:border-saffron"}`}>
                    {t === "B2B" ? "Jeweller / Trade" : "Personal"}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <Field id="name" label="Your name *" value={form.name} onChange={set("name")} placeholder="Full name" required />
                <Field id="phone" label={<>Phone / <WhatsAppIcon size={12} className="text-[#25D366]" /> *</>} value={form.phone} onChange={set("phone")} placeholder="+91 …" required />
                <Field id="email" label="Email (optional)" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
                <Field id="lot_id" label="Lot ID (optional)" value={form.lot_id} onChange={set("lot_id")} placeholder="e.g. PGJ-S012" />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <FilterSelect id="enquiry-shape" label="Shape" value={form.shape} onChange={set("shape")} options={options?.shapes || []} placeholder="Select shape" />
                <FilterSelect id="enquiry-sieve" label="Sieve · Approx. mm" value={form.sieve} onChange={(v) => setForm((f) => ({ ...f, sieve: v, carat_range: v ? "melee" : f.carat_range }))} options={options?.sieves || []} placeholder="Select size" />
                <FilterSelect id="enquiry-clarity" label="Clarity" value={form.clarity} onChange={set("clarity")} options={options?.clarities || []} placeholder="Select clarity" />
                <FilterSelect id="enquiry-carat" label="Carat range" value={form.carat_range} onChange={(v) => setForm((f) => ({ ...f, carat_range: v, sieve: v && v !== "melee" ? "" : f.sieve }))} options={options?.carats || []} placeholder="Select carat range" />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <Field id="quantity_carats" label="Quantity (carats)" type="number" step="0.01" min="0" value={form.quantity_carats} onChange={set("quantity_carats")} placeholder="e.g. 50" />
                <div className="flex flex-col gap-2">
                  <label htmlFor="message" className="eyebrow">Message</label>
                  <textarea id="message" data-testid="enquiry-message" rows={3} className="field resize-none" value={form.message} onChange={set("message")} placeholder="Colour preference, budget, deadline…" />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button type="submit" disabled={sending} data-testid="enquiry-submit-btn" className="btn-gold disabled:opacity-50">
                  <WhatsAppIcon size={16} /> {sending ? "Saving…" : <>Save &amp; open <WhatsAppIcon size={16} /></>}
                </button>
                <a href={waLink(buildMessage())} target="_blank" rel="noreferrer" data-testid="enquiry-direct-whatsapp-btn" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-ivory/50 hover:text-saffron transition-colors">Skip form, just <WhatsAppIcon size={15} className="text-[#25D366]" /> →</a>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </main>
  );
}
